/**
 * Shared child/outlet shape for parent + legacy flows.
 * Child `id` is OutletCustomer id (not CRM member id / link id).
 */

/** Stable empty list for selectors (avoids new [] each render). */
export const EMPTY_CHILDREN = Object.freeze([]);

export function normalizeOutlet(outlet) {
	if (outlet == null || outlet === false) return null;
	if (typeof outlet === 'number' || typeof outlet === 'string') {
		return { id: Number(outlet), name: '' };
	}
	if (typeof outlet === 'object' && outlet.id != null) {
		return {
			id: Number(outlet.id),
			name: outlet.name != null ? String(outlet.name) : '',
		};
	}
	return null;
}

export function normalizeChild(raw) {
	if (!raw || raw.id == null) return null;

	// Admin link payload nests the customer; me/ returns the customer flat.
	const source = raw.customer && raw.customer.id != null ? raw.customer : raw;

	// Parent↔child link flag (me/ + dashboard). Prefer link-level, then customer-level.
	// Omit when absent so getCustomer balance refreshes do not wipe a known false.
	const familyRaw =
		raw.family_is_active !== undefined
			? raw.family_is_active
			: source.family_is_active !== undefined
				? source.family_is_active
				: undefined;

	return {
		id: Number(source.id),
		member: source.member
			? {
					id: source.member.id,
					name: source.member.name ?? '',
					on_list_attributes: source.member.on_list_attributes ?? {},
				}
			: { id: null, name: '', on_list_attributes: {} },
		outlet: normalizeOutlet(source.outlet),
		balance:
			source.balance == null
				? '0.000'
				: typeof source.balance === 'number'
					? source.balance.toFixed(3)
					: String(source.balance),
		is_active: Boolean(source.is_active),
		...(familyRaw !== undefined ? { family_is_active: Boolean(familyRaw) } : {}),
	};
}

export function normalizeChildren(list) {
	if (!Array.isArray(list)) return [];
	return list.map(normalizeChild).filter(Boolean);
}

export function normalizeParentMe(data) {
	const children = normalizeChildren(data?.children);
	const outlet =
		normalizeOutlet(data?.outlet) ||
		children.find((c) => c.outlet)?.outlet ||
		normalizeOutlet(data?.customer?.outlet);

	return {
		account_type: data?.account_type ?? (data?.customer ? 'legacy' : 'parent'),
		user: data?.user ?? null,
		customer: data?.customer ?? null,
		outlet,
		children,
	};
}
